import { queryOptions } from '@tanstack/react-query';
import {
  adminApi,
  type AdminUserListItem,
  type Asset,
  type Audit,
  type Company,
  type CompanyDocument,
  type CorrectiveAction,
  type CorrectiveActionStatus,
  type DocumentType,
  type DocumentVerificationStatus,
  type Driver,
  type Question,
  type QuestionCategory,
  type Questionnaire,
  type TrainingModule,
  type VerificationQueueItem,
} from '@l2u/web-shared';
import type { BundleSummary } from '@l2u/shared/complianceBundles';
import { myAuditsQueryKey } from '../admin/myAuditsFilter.ts';
import { reviewQueueCountQueryKey, type ReviewQueueEntry } from './pendingWork.ts';

/**
 * One query definition per admin endpoint, so the SAME URL is cached under the
 * SAME key on every page that reads it.
 *
 * Before these, /api/companies alone lived under three keys (['companies'] on
 * the dashboard and detail pages, ['companies', { region }] on the list pages,
 * ['companies', 'forReports']) — three cache entries, and so three ~1s requests,
 * for one response. Every key here keeps the first element its old keys had, so
 * every existing `invalidateQueries({ queryKey: ['companies'] })`-style prefix
 * invalidation (and reviewRefresh.ts's filters) still reaches it.
 *
 * Each queryFn hands TanStack's AbortSignal to the request, so a query nobody
 * is waiting for any more (the page was left) is cancelled rather than finished
 * and discarded. It takes effect once adminApi forwards `signal` to fetch.
 */

/**
 * For reference data — the question bank, categories, documents, document
 * types, team regions, users, the questionnaire picker. These change only when
 * an admin edits them, every such edit in this app invalidates the matching
 * key, and they are read by most detail pages: refetching them on every page
 * open (the 30s default) was a request each, every time, for nothing.
 */
export const REFERENCE_STALE_TIME = 10 * 60_000;

const regionQuery = (region: string | null) => (region ? { team_region_id: region } : {});

// ── Owner lists ────────────────────────────────────────────────────────────

/**
 * GET /api/companies, region-scoped when a region is active. `region: null` is
 * the unscoped list the dashboard, the detail pages' owner pickers and Reports
 * read — and also what the Companies page reads with no region selected, so
 * opening it from the dashboard is served from cache.
 */
function companiesQuery(region: string | null) {
  return queryOptions({
    queryKey: ['companies', { region }] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ companies: Company[] }>('/api/companies', {
        query: regionQuery(region),
        signal,
      }),
  });
}

/** GET /api/drivers (every company's drivers), region-scoped like companiesQuery. */
function driversQuery(region: string | null) {
  return queryOptions({
    // 'all' kept from the old keys: Drivers.tsx invalidates ['drivers', 'all'].
    queryKey: ['drivers', 'all', { region }] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ drivers: Driver[] }>('/api/drivers', { query: regionQuery(region), signal }),
  });
}

/** GET /api/assets, region-scoped like companiesQuery. */
function assetsQuery(region: string | null) {
  return queryOptions({
    queryKey: ['assets', { region }] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ assets: Asset[] }>('/api/assets', { query: regionQuery(region), signal }),
  });
}

/**
 * GET /api/companies/:id — CompanyDetail's record, under the key the review
 * helpers address as ownerRecordKey(). staleTime 0, unlike the app's 30s:
 * its driver_attention_count (the Drivers-tab badge) moves when a DRIVER of
 * this company is deactivated, reviewed or published on another page, and
 * nothing there invalidates the company. Refetching on every visit costs
 * nothing extra on a first open (it is fetched then anyway) and keeps the
 * badge true on a return.
 */
function companyRecordQuery(id: string) {
  return queryOptions({
    queryKey: ['company', id] as const,
    queryFn: ({ signal }) => adminApi.get<{ company: Company }>(`/api/companies/${id}`, { signal }),
    staleTime: 0,
    enabled: !!id,
  });
}

// ── Reference data ─────────────────────────────────────────────────────────

/** GET /api/questions — the whole question bank. */
function allQuestionsQuery() {
  return queryOptions({
    queryKey: ['questions', 'all'] as const,
    queryFn: ({ signal }) => adminApi.get<{ questions: Question[] }>('/api/questions', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** GET /api/questions?questionnaire_id= — one questionnaire's questions. */
function questionnaireQuestionsQuery(questionnaireId: string) {
  return queryOptions({
    queryKey: ['questions', questionnaireId] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ questions: Question[] }>('/api/questions', {
        query: { questionnaire_id: questionnaireId },
        signal,
      }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** GET /api/question-categories — every category, in global order. */
function allQuestionCategoriesQuery() {
  return queryOptions({
    queryKey: ['question-categories', 'all'] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** GET /api/question-categories?questionnaire_id= — one questionnaire's, in its join order. */
function questionnaireCategoriesQuery(questionnaireId: string) {
  return queryOptions({
    queryKey: ['question-categories', questionnaireId] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories', {
        query: { questionnaire_id: questionnaireId },
        signal,
      }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** GET /api/documents. */
function documentsQuery() {
  return queryOptions({
    queryKey: ['documents'] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ documents: CompanyDocument[] }>('/api/documents', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** GET /api/document-types. */
function documentTypesQuery() {
  return queryOptions({
    queryKey: ['document-types'] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ document_types: DocumentType[] }>('/api/document-types', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** GET /api/questionnaires — the plain list the builder and Reports pick from. */
function questionnairesQuery() {
  return queryOptions({
    queryKey: ['questionnaires'] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ questionnaires: Questionnaire[] }>('/api/questionnaires', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

/**
 * GET /api/training — every training module, in display order. No reference
 * staleTime: the Training page has always refetched it on entry.
 */
function trainingModulesQuery() {
  return queryOptions({
    queryKey: ['training'] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ training_modules: TrainingModule[] }>('/api/training', { signal }),
  });
}

/** The compliance bundle list's key (Settings → Compliance bundles invalidates it). */
export const COMPLIANCE_BUNDLES_KEY = ['compliance-bundles'] as const;

/**
 * GET /api/compliance-bundles (admin-only on the API): Settings → Compliance
 * bundles, and the detail pages' "Assign bundle" picker.
 */
function complianceBundlesQuery() {
  return queryOptions({
    queryKey: COMPLIANCE_BUNDLES_KEY,
    queryFn: ({ signal }) =>
      adminApi.get<{ bundles: BundleSummary[] }>('/api/compliance-bundles', { signal }),
  });
}

/** GET /api/users (admin-only on the API). */
function usersQuery() {
  return queryOptions({
    queryKey: ['users'] as const,
    queryFn: ({ signal }) => adminApi.get<{ users: AdminUserListItem[] }>('/api/users', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

// ── Work queues (a page and a nav badge read the same list) ───────────────

/**
 * GET /api/corrective-actions?status= — the Corrective Actions page's tabs. The
 * 'responded' tab is ALSO what the tab bar's red badge and the dashboard's
 * banner count (with `select`), so opening the page from either is a cache hit
 * and the three can never disagree.
 *
 * The API leaves out every action of a deactivated owner unless
 * `include_deactivated=1` — the page's "Include deactivated" box. That variant
 * gets its own key, so ticking the box never feeds the badge's cache entry.
 */
function correctiveActionsQuery(tab: CorrectiveActionStatus | 'all', includeDeactivated = false) {
  return queryOptions({
    queryKey: includeDeactivated
      ? (['corrective-actions', tab, 'with-deactivated'] as const)
      : (['corrective-actions', tab] as const),
    queryFn: ({ signal }) =>
      adminApi.get<{ corrective_actions: CorrectiveAction[] }>('/api/corrective-actions', {
        query: {
          ...(tab === 'all' ? {} : { status: tab }),
          ...(includeDeactivated ? { include_deactivated: '1' } : {}),
        },
        signal,
      }),
  });
}

/**
 * GET /api/audits/mine — the My audits page. Unticked it is the shared
 * ['my-audits'] entry (badge, dashboard feed); "Include deactivated" sends
 * `include_deactivated=1` under its own child key (myAuditsQueryKey).
 */
function myAuditsQuery(includeDeactivated: boolean) {
  return queryOptions({
    queryKey: myAuditsQueryKey(includeDeactivated),
    queryFn: ({ signal }) =>
      adminApi.get<{ audits: Audit[] }>('/api/audits/mine', {
        query: includeDeactivated ? { include_deactivated: '1' } : {},
        signal,
      }),
  });
}

/**
 * GET /api/verifications?status= — the Verifications page's tabs. The default
 * 'needs_review' tab is also the More screen's badge count (usePendingWork).
 */
function verificationsQuery(tab: DocumentVerificationStatus | 'all') {
  return queryOptions({
    queryKey: ['verifications', tab] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ verifications: VerificationQueueItem[] }>('/api/verifications', {
        query: tab === 'all' ? {} : { status: tab },
        signal,
      }),
  });
}

/**
 * GET /api/review-queue with every filter at its default (all entities, every
 * assignee) except the active region — the Queue page's unfiltered request.
 * One entry behind the mobile queue count, the sidebar's Drivers/Assets counts
 * and the lists' "Needs review" pills (usePendingWork.ts), so they all read the
 * queue's own membership and cost one request between them, never one per row.
 */
function reviewQueueQuery(region: string | null) {
  return queryOptions({
    queryKey: reviewQueueCountQueryKey(region),
    queryFn: ({ signal }) =>
      adminApi.get<{ items: ReviewQueueEntry[] }>('/api/review-queue', {
        query: regionQuery(region),
        signal,
      }),
  });
}

/**
 * The factories, namespaced so a page can keep its local `documentsQuery` /
 * `questionsQuery` names: `const documentsQuery = useQuery(adminQueries.documents())`.
 * Spread into useQuery to add `select` / `enabled`.
 */
export const adminQueries = {
  companies: companiesQuery,
  companyRecord: companyRecordQuery,
  drivers: driversQuery,
  assets: assetsQuery,
  allQuestions: allQuestionsQuery,
  questionnaireQuestions: questionnaireQuestionsQuery,
  allQuestionCategories: allQuestionCategoriesQuery,
  questionnaireCategories: questionnaireCategoriesQuery,
  documents: documentsQuery,
  documentTypes: documentTypesQuery,
  questionnaires: questionnairesQuery,
  trainingModules: trainingModulesQuery,
  complianceBundles: complianceBundlesQuery,
  users: usersQuery,
  correctiveActions: correctiveActionsQuery,
  myAudits: myAuditsQuery,
  verifications: verificationsQuery,
  reviewQueue: reviewQueueQuery,
};
